import { Component, effect, inject, input, OnDestroy, output, SecurityContext, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { DomSanitizer } from '@angular/platform-browser';
import { combineLatest, debounceTime, distinctUntilChanged, filter, of, startWith, switchMap, take, tap } from 'rxjs';
import { AddressLookupService } from '../../services/address-lookup.service';
import { Address } from '../../models/address';
import { AddressService } from 'src/app/core/services/address.service';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { FormFieldLoadingComponent } from 'ok-angular-shared/components';
import { Guid } from 'guid-typescript';

interface IDepAddressTypeaheadForm {
  addressLookup: FormControl<string | null>;
  addressWithoutValidation: FormControl<string | null>;
}

export class AddressChanged {
  darId?: Guid | null;
  fullAddress?: string | null;
  address?: Address | null;
  formatAddressToString?: string | null;
}

export interface InitialAddress {
  darId?: Guid;
  fullAddress?: string;
}

@Component({
  selector: 'adm-dep-address-typeahead',
  imports: [
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatAutocompleteModule,
    CustomDropDownArrowDirective,
    ReactiveFormsModule,
    FormFieldLoadingComponent,
  ],
  templateUrl: './dep-address-typeahead.component.html',
  styleUrl: './dep-address-typeahead.component.scss',
})
export class DepAddressTypeaheadComponent implements OnDestroy {
  private sanitizer = inject(DomSanitizer);
  private addressLookupService = inject(AddressLookupService);
  public addressService = inject(AddressService);

  addressWithoutDARValidation = input.required<boolean>();
  initialAddress = input<InitialAddress | null>(null);
  label = input<string | null>('Adresse');
  formRow = input<boolean>(true);
  hideRequiredMarker = input<boolean>(false);
  loading = input<boolean>(false);
  addressSelected = output<AddressChanged | null>();
  parentFormGroup = input<FormGroup>();
  childForm = new FormGroup<IDepAddressTypeaheadForm>({
    addressLookup: new FormControl<string | null>(null, [Validators.required, this.addressSelectionValidator()]),
    addressWithoutValidation: new FormControl<string | null>(null),
  });

  filteredAddresses = signal<Address[]>([]);
  addressOptionSelected = signal<boolean>(false);
  constructor() {
    effect(() => {
      const parentForm = this.parentFormGroup();
      if (parentForm) {
        parentForm.addControl('addressLookup', this.childForm.controls.addressLookup);
        parentForm.addControl('addressWithoutValidation', this.childForm.controls.addressWithoutValidation);
        parentForm.updateValueAndValidity();
      }
    });
    toObservable(this.initialAddress)
      .pipe(
        filter(address => address !== null && address !== undefined),
        tap(address => {
          const addressWithoutDARValidation = this.addressWithoutDARValidation();
          this.addressOptionSelected.set(true);
          if (addressWithoutDARValidation) {
            this.childForm.patchValue({ addressWithoutValidation: address.fullAddress });
          } else {
            this.childForm.patchValue({ addressLookup: address.fullAddress });
            this.childForm.controls.addressLookup.updateValueAndValidity();
          }
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    this.childForm.statusChanges
      .pipe(
        tap(() => {
          const parentForm = this.parentFormGroup();
          if (parentForm) {
            parentForm.updateValueAndValidity();
          }
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    const parentForm = this.parentFormGroup();
    if (parentForm) {
      parentForm.removeControl('addressLookup');
      parentForm.removeControl('addressWithoutValidation');
      parentForm.updateValueAndValidity();
    }
  }

  withoutDarValidatorChanged = toObservable(this.addressWithoutDARValidation)
    .pipe(
      tap(withoutDarValidation => {
        this.filteredAddresses.set([]);
        if (withoutDarValidation) {
          this.childForm.patchValue({ addressLookup: null });
          this.addressChanged(null);
          this.childForm.controls.addressLookup.disable();
          this.childForm.controls.addressWithoutValidation.enable();
          this.childForm.controls.addressLookup.clearValidators();
          this.childForm.controls.addressLookup.updateValueAndValidity();
          this.childForm.controls.addressWithoutValidation.setValidators([Validators.required]);
          this.childForm.controls.addressWithoutValidation.updateValueAndValidity();
        } else {
          this.childForm.controls.addressLookup.enable();
          this.childForm.controls.addressWithoutValidation.disable();
          this.childForm.controls.addressWithoutValidation.clearValidators();
          this.childForm.controls.addressWithoutValidation.updateValueAndValidity();
          this.childForm.controls.addressLookup.setValidators([Validators.required, this.addressSelectionValidator()]);
          this.childForm.controls.addressLookup.updateValueAndValidity();
        }
      }),
      takeUntilDestroyed()
    )
    .subscribe();

  addressFieldChanged = combineLatest([
    this.childForm.controls.addressLookup.valueChanges.pipe(
      startWith(''), // let one blank through to search for all
      filter(() => !this.addressOptionSelected()),
      tap(() => this.addressOptionSelected.set(false)),
      debounceTime(300),
      distinctUntilChanged(),
      tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
    ),
    toObservable(this.addressWithoutDARValidation),
  ])
    .pipe(
      switchMap(([val, withoutDarValidation]) => {
        this.addressOptionSelected.set(false);
        if (!val) {
          this.filteredAddresses.set([]);
          return of(null);
        }
        if (withoutDarValidation) {
          return of(null);
        }
        return this.addressLookupService.lookup(val).pipe(
          tap(addresses => {
            this.filteredAddresses.set(addresses);
          })
        );
      }),
      takeUntilDestroyed()
    )
    .subscribe();

  addressWithoutValidationFieldChanged = combineLatest([
    this.childForm.controls.addressWithoutValidation.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
    ),
    toObservable(this.addressWithoutDARValidation),
  ])
    .pipe(
      tap(([val, withoutDarValidation]) => {
        if (withoutDarValidation) {
          this.addressSelected.emit({ darId: null, fullAddress: val, formatAddressToString: val });
        }
      }),
      takeUntilDestroyed()
    )
    .subscribe();

  displayAddress = (address: Address | null | string): string => {
    if (typeof address === 'string') {
      return address;
    }
    return address ? this.addressService.formatAddressToString(address) : '';
  };
  private addressSelectionValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      if (!this.addressOptionSelected()) {
        return { addressNotSelected: true };
      }
      return null;
    };
  }
  addressChanged = (address: Address | null) => {
    if (address !== null) {
      this.addressOptionSelected.set(true);
      this.childForm.controls.addressWithoutValidation.setValue(this.displayAddress(address), { emitEvent: false });
      this.childForm.controls.addressLookup.updateValueAndValidity();
    }
    this.addressSelected.emit({
      darId: address?.darId ? Guid.parse(address.darId) : null,
      fullAddress: address?.fullAddress,
      address: address,
      formatAddressToString: this.displayAddress(address),
    });
  };
}
